import { db } from '../firebaseFunctions/config';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isDemoMode } from '../demo/isDemoMode';
import demoStore from '../demo/demoStore';

// Fee constants — must match Cloud Function
export const TICKETING_FEE_PERCENT = 3.7;
export const TICKETING_FEE_FIXED = 1.79;

export const computeFees = (priceUSD) => {
  const fee = parseFloat((priceUSD * TICKETING_FEE_PERCENT / 100 + TICKETING_FEE_FIXED).toFixed(2));
  const net = parseFloat((priceUSD - fee).toFixed(2));
  return { fee, net };
};

// ── Ticket Types (subcollection under events/{eventId}/ticketTypes) ──────────

export const getTicketTypes = async (eventId) => {
  if (isDemoMode) {
    const docs = demoStore.getDocs(`events/${eventId}/ticketTypes`);
    return { success: true, ticketTypes: docs.map(d => ({ id: d.id, ...d.data })) };
  }
  try {
    const snap = await getDocs(collection(db, 'events', eventId, 'ticketTypes'));
    return { success: true, ticketTypes: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const saveTicketType = async (eventId, ticketTypeId, data) => {
  if (isDemoMode) {
    const id = ticketTypeId || `ticket-${Date.now()}`;
    const existing = ticketTypeId ? (demoStore.getDoc(`events/${eventId}/ticketTypes`, ticketTypeId) || {}) : {};
    demoStore.setDoc(`events/${eventId}/ticketTypes`, id, {
      ...existing,
      ...data,
      soldCount: existing.soldCount || 0,
      updatedAt: new Date(),
      createdAt: existing.createdAt || new Date(),
    });
    return { success: true, id };
  }
  try {
    const payload = { ...data, updatedAt: serverTimestamp() };
    if (ticketTypeId) {
      await updateDoc(doc(db, 'events', eventId, 'ticketTypes', ticketTypeId), payload);
      return { success: true, id: ticketTypeId };
    } else {
      payload.soldCount = 0;
      payload.createdAt = serverTimestamp();
      const ref = await addDoc(collection(db, 'events', eventId, 'ticketTypes'), payload);
      return { success: true, id: ref.id };
    }
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const deactivateTicketType = async (eventId, ticketTypeId) => {
  if (isDemoMode) {
    const existing = demoStore.getDoc(`events/${eventId}/ticketTypes`, ticketTypeId) || {};
    demoStore.setDoc(`events/${eventId}/ticketTypes`, ticketTypeId, { ...existing, isActive: false, updatedAt: new Date() });
    return { success: true };
  }
  try {
    await updateDoc(doc(db, 'events', eventId, 'ticketTypes', ticketTypeId), { isActive: false, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Ticket Sales Dashboard ───────────────────────────────────────────────────

export const getTicketSales = async (eventId, eoUserId) => {
  if (isDemoMode) {
    const allPurchases = demoStore.getDocs('ticketPurchases', [
      { type: 'where', field: 'eventId', op: '==', value: eventId },
    ]);
    const purchases = allPurchases.map(p => ({ id: p.id, ...p.data }));
    const totals = purchases.reduce((acc, p) => {
      if (p.status === 'completed') {
        acc.sold += p.quantity || 1;
        acc.gross += (p.priceAtPurchase || 0) * (p.quantity || 1);
        acc.fees += p.pebbleFeeTotal || 0;
        acc.net += p.eoNetAmount || 0;
      }
      return acc;
    }, { sold: 0, gross: 0, fees: 0, net: 0 });
    return { success: true, purchases, totals };
  }
  try {
    const q = query(
      collection(db, 'ticketPurchases'),
      where('eventId', '==', eventId),
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    const snap = await getDocs(q);
    const purchases = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    const totals = purchases.reduce((acc, p) => {
      if (p.status === 'completed') {
        acc.sold += p.quantity || 1;
        acc.gross += p.priceAtPurchase || 0;
        acc.fees += p.pebbleFeeTotal || 0;
        acc.net += p.eoNetAmount || 0;
      }
      return acc;
    }, { sold: 0, gross: 0, fees: 0, net: 0 });

    return { success: true, purchases, totals };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Stripe Checkout for Ticket Purchase ─────────────────────────────────────

export const createTicketCheckoutSession = async ({ eventId, ticketTypeId, buyerName, buyerEmail, promoCode }) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'createTicketCheckoutSession');
    const result = await fn({ eventId, ticketTypeId, buyerName, buyerEmail, promoCode });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Free Ticket Claim ────────────────────────────────────────────────────────

export const claimFreeTicket = async ({ eventId, ticketTypeId, buyerName, buyerEmail }) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'claimFreeTicket');
    const result = await fn({ eventId, ticketTypeId, buyerName, buyerEmail });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Fetch ticket by purchase ID (success page) ───────────────────────────────

export const getTicketByPurchaseId = async (purchaseId) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'getTicketByPurchaseId');
    const result = await fn({ purchaseId });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Waitlist ─────────────────────────────────────────────────────────────────

export const joinWaitlist = async ({ eventId, ticketTypeId, name, email }) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'joinTicketWaitlist');
    const result = await fn({ eventId, ticketTypeId, name, email });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Check-in ─────────────────────────────────────────────────────────────────

export const checkInTicket = async ({ purchaseId, eventId }) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'checkInTicket');
    const result = await fn({ purchaseId, eventId });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const getEventAttendees = async (eventId) => {
  try {
    const fns = getFunctions();
    const fn = httpsCallable(fns, 'getEventAttendees');
    const result = await fn({ eventId });
    return result.data;
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ── Public: get active ticket types for an event (no auth required) ──────────

export const getPublicTicketTypes = async (eventId) => {
  try {
    const now = Timestamp.now();
    const snap = await getDocs(collection(db, 'events', eventId, 'ticketTypes'));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Include sold-out types so the EventPage can show waitlist option
    const active = all.filter(t => {
      if (!t.isActive) return false;
      if (t.saleStartDate && t.saleStartDate.toMillis() > now.toMillis()) return false;
      if (t.saleEndDate && t.saleEndDate.toMillis() < now.toMillis()) return false;
      return true;
    });
    return { success: true, ticketTypes: active };
  } catch (err) {
    return { success: false, error: err.message };
  }
};
